import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import ActiveDealsTable, { DealRow } from './ActiveDealsTable';
import OpenTasksBox from './OpenTasksBox';
import ContactStatusBox from './ContactStatusBox';
import UpcomingCommitmentsBox, { CommitmentItem } from './UpcomingCommitmentsBox';
import { HuddleClientService } from 'service/huddle-client.service';
import { ClientPrioritiesResponse } from 'model/client.model';

const PrioritiesTab = ({ accordionExpanded = false }: { accordionExpanded?: boolean }) => {
  const location = useLocation();
  const splitPath = location.pathname?.split('/');
  const clientId = splitPath[3];

  const [data, setData] = useState<ClientPrioritiesResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      if (!clientId) return;
      setLoading(true);
      try {
        const res = await HuddleClientService.getClientPriorities(clientId);
        setData(res);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [clientId]);

  const deals: DealRow[] = (data?.activeDeals ?? []).map((d) => ({
    id: d.dealId,
    name: d.name,
    value: d.dealValue,
    status: d.dealStageName,
    days: d.daysActive,
  }));

  const tasks = (data?.openTasks ?? []).map((t) => ({
    id: t.taskId,
    subject: t.subject,
    status: t.status,
    priority: t.priority,
    date: t.dueDate,
    assignTo: { Name: t.assignedTo },
  }));

  const commitments: CommitmentItem[] = data?.upcomingCommitments ?? [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: { xs: '16px', xl: '24px' },
        alignItems: 'stretch',
        padding: { xs: '16px 0', xl: '20px 0' },
      }}
    >
      {/* Left: Active Deals table — flex 3 */}
      <Box
        sx={{
          flex: 3,
          minWidth: 0,
          height: accordionExpanded ? 'calc(100vh - 520px)' : 'calc(100vh - 380px)',
          overflowY: 'auto',
        }}
      >
        <ActiveDealsTable deals={deals} loading={loading} />
      </Box>

      {/* Right: stacked info boxes — flex 1 */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          minWidth: 0,
          height: accordionExpanded ? 'calc(100vh - 520px)' : 'calc(100vh - 380px)',
          overflowY: 'auto',
        }}
      >
        <OpenTasksBox tasks={tasks} loading={loading} />
        <ContactStatusBox contractStatus={data?.contractStatus ?? null} />
        <UpcomingCommitmentsBox items={commitments} />
      </Box>
    </Box>
  );
};

export default PrioritiesTab;
